import { round } from 'lodash'
import {
  HourSeriesResult,
  InputHourSerieType,
} from '../building-energy-model/hour-series-calculator'
import {
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
  CoolingParameters,
  EnergySystemFragment,
  EnergySystemType,
  HourSeriesType,
} from '../generated/graphql'
import { calculateHourSerieFactors } from '../schedule/hour-series-factors-calculator'
import { hoursInYear } from '../util/hours-in-year'
import { calcVentilationHourSeries } from './ventilation-hour-series-calculator'
import { i18n } from '../i18n'

export const coolingCalculator = (
  outsideTempHourSerie: number[],
  ahuCoolingDemandSerie: number[],
  energySystem: EnergySystemFragment,
  energyModelArea: number,
  year: number,
): HourSeriesResult[] => {
  const params = energySystem.parameters as CoolingParameters

  let systemCoolingSerie = Array.from(Array(hoursInYear).fill(0)) as number[]
  if (energySystem.schedule && params.systemCoolingPower !== 0) {
    // Initialize with the schedule factors serie
    systemCoolingSerie = calculateHourSerieFactors(energySystem.schedule, year)

    for (let i = 0; i < systemCoolingSerie.length; i++) {
      systemCoolingSerie[i] *=
        round(
          params.systemCoolingPower / params.scDistributionEfficiency / 1000,
          2,
        ) * 1000
    }
  }

  const spaceCoolingSerie = Array.from(Array(hoursInYear).fill(0)) as number[]
  const ahuCoolingSerie = Array.from(Array(hoursInYear).fill(0)) as number[]

  for (let i = 0; i < outsideTempHourSerie.length; i++) {
    const currTemp = outsideTempHourSerie[i]

    const deltaScBase = Math.max(0, currTemp - params.scStartTemp)

    const eigenScBase = deltaScBase * params.scIncreaseSlope

    const deltaScExtra = Math.max(0, currTemp - params.scExtraCoefStartTemp)

    const eigenScExtra = deltaScExtra * params.scIncreaseExtraCoef

    spaceCoolingSerie[i] =
      round(
        ((eigenScBase + eigenScExtra) *
          energyModelArea *
          params.scSpacesPortion) /
          params.scDistributionEfficiency /
          1000,
        2,
      ) * 1000

    const deltaAcExtra = currTemp - params.acExtraCoefStartTemp

    ahuCoolingSerie[i] =
      round(
        (ahuCoolingDemandSerie[i] *
          (1 + (deltaAcExtra / 5) * params.acIncreaseExtraCoef) *
          params.acCorrectionCoef) /
          params.acDistributionEfficiency,
        2,
      ) * 1000
  }

  const resultSeries: HourSeriesResult[] = []

  resultSeries.push(
    {
      hourSerieType: HourSeriesType.CoolingDemand,
      energySystemType: EnergySystemType.Cooling,
      consumptionEnergyType: ConsumptionEnergyType.SystemCooling,
      hourSerieName: i18n.t(
        'buildingEnergyModels.hourSeriesNames.systemCooling',
      ),
      hourSerie: systemCoolingSerie,
    },
    {
      hourSerieType: HourSeriesType.CoolingDemand,
      energySystemType: EnergySystemType.Cooling,
      consumptionEnergyType: ConsumptionEnergyType.SpaceCooling,
      hourSerieName: i18n.t(
        'buildingEnergyModels.hourSeriesNames.spaceCooling',
      ),
      hourSerie: spaceCoolingSerie,
    },
    {
      hourSerieType: HourSeriesType.CoolingDemand,
      energySystemType: EnergySystemType.Cooling,
      consumptionEnergyType: ConsumptionEnergyType.AhuCooling,
      hourSerieName: i18n.t('buildingEnergyModels.hourSeriesNames.ahuCooling'),
      hourSerie: ahuCoolingSerie,
    },
  )

  return resultSeries
}

export const calcNetCoolingDensityHourSerie = (
  model: BuildingEnergyModelFragment,
  outsideTempHourSerie: number[],
  area: number,
  year: number,
): number[] => {
  const defaultResult = Array<number>(hoursInYear).fill(0)

  const cooling = model.energySystems.find(
    (s) => s.type === EnergySystemType.Cooling,
  )

  if (!cooling) return defaultResult

  const ventilationResult = calcVentilationHourSeries(
    model,
    outsideTempHourSerie,
    year,
  )

  const ahuCoolingDemandSerie =
    ventilationResult.inputs.find(
      (r) => r.inputSerieType === InputHourSerieType.VentilationCoolingDemand,
    )?.hourSerie ?? Array<number>(hoursInYear).fill(0)

  const coolingHourSeries = coolingCalculator(
    outsideTempHourSerie,
    ahuCoolingDemandSerie,
    cooling,
    area,
    year,
  )

  const coolingDensitySerie = coolingHourSeries[0].hourSerie.map((i, index) =>
    round(
      (i +
        coolingHourSeries[1].hourSerie[index] +
        coolingHourSeries[2].hourSerie[index]) /
        area,
      2,
    ),
  )

  return coolingDensitySerie
}
